import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FilterInDropdown } from 'atoms/dropdowns/FilterInDropdown';
import { ScarcityIcon } from 'atoms/icons/ScarcityIcon';
import { RadioGroup } from 'atoms/inputs/RadioGroup';
import { FullWidthContainer } from 'atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyM, HeadlineS, LabelS } from 'atoms/typography';
import { scarcityMessages } from 'lib/scarcity';
import { format as formatSeason } from 'lib/seasons';

import { CardSupply_anyPlayerInterface } from './__generated__/index.graphql';

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
`;

const ScarcitySupplyDisplay = ({
  scarcity,
  supply,
}: {
  scarcity: 'limited' | 'rare' | 'super_rare' | 'unique';
  supply: number;
}) => {
  return (
    <Vertical gap={0}>
      <LabelS as="p" color="var(--c-nd-600)">
        <FormattedMessage {...scarcityMessages[scarcity]} />
      </LabelS>
      <Horizontal gap={1}>
        <ScarcityIcon scarcity={scarcity} />
        <BodyM as="p">{supply}</BodyM>
      </Horizontal>
    </Vertical>
  );
};

type internalSupply = {
  limited: number;
  rare: number;
  superRare: number;
  unique: number;
};

const SupplyDisplay = ({ supply }: { supply: internalSupply | undefined }) => {
  if (!supply) return null;

  return (
    <Grid>
      <ScarcitySupplyDisplay scarcity="limited" supply={supply.limited} />
      <ScarcitySupplyDisplay scarcity="rare" supply={supply.rare} />
      <ScarcitySupplyDisplay scarcity="super_rare" supply={supply.superRare} />
      <ScarcitySupplyDisplay scarcity="unique" supply={supply.unique} />
    </Grid>
  );
};

type Props = {
  anyPlayer: CardSupply_anyPlayerInterface;
};

export const CardSupply = ({ anyPlayer }: Props) => {
  const { cardSupply } = anyPlayer;

  const [season, setSeason] = useState<string | undefined>();

  if (cardSupply.length === 0) return null;

  const options = [...cardSupply]
    .sort((a, b) => b.season.startYear - a.season.startYear)
    .map(c => ({
      label: formatSeason(c.season.startYear),
      value: c.season.startYear.toString(),
    }));

  const selectedOption = options.find(o => o.value === season) || options[0];

  const selectedSupply = cardSupply.find(
    a => a.season.startYear.toString() === selectedOption.value
  );

  return (
    <FullWidthContainer>
      <Vertical gap={1.5}>
        <HeadlineS as="h2">
          <FormattedMessage
            id="Seo.Player.CardsSupply.cardsSupply"
            defaultMessage="Cards supply"
          />
        </HeadlineS>

        <div>
          <FilterInDropdown buttonLabel={selectedOption.label}>
            {({ closeDropdown }) => (
              <RadioGroup
                modalSelect
                options={options}
                name="card-supply-sort-type"
                onChange={value => {
                  setSeason(value);
                  closeDropdown();
                }}
              />
            )}
          </FilterInDropdown>
        </div>
        <SupplyDisplay supply={selectedSupply} />
      </Vertical>
    </FullWidthContainer>
  );
};

CardSupply.fragments = {
  anyPlayer: gql`
    fragment CardSupply_anyPlayerInterface on AnyPlayerInterface {
      slug
      cardSupply {
        limited
        rare
        superRare
        unique
        season {
          startYear
        }
      }
    }
  ` as TypedDocumentNode<CardSupply_anyPlayerInterface>,
};
